import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Gem, Gift, Heart } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fireworks } from '@/lib/confetti'
import Reveal from '@/components/landing/reveal'
import { FINALE } from '@/constants/birthday'

type Point = {
    x: number
    y: number
}

// Fixed star field, the same on the server and in the browser.
const STARS = Array.from({ length: 36 }, (_, index) => ({
    top: `${(index * 37) % 100}%`,
    left: `${(index * 61 + 7) % 100}%`,
    size: index % 5 === 0 ? 3 : 2,
    delay: `${(index % 9) * 0.4}s`
}))

const FinaleSection: FC = (): ReactNode => {
    const [answered, setAnswered] = useState(false)
    const [noPosition, setNoPosition] = useState<Point | null>(null)
    const arenaRef = useRef<HTMLDivElement>(null)
    const yesRef = useRef<HTMLButtonElement>(null)
    const noRef = useRef<HTMLButtonElement>(null)
    const stopFireworks = useRef<(() => void) | null>(null)

    useEffect(() => () => stopFireworks.current?.(), [])

    // The "Nahi" button runs away every time she tries to reach it.
    const dodge = (): void => {
        const arena = arenaRef.current
        const button = noRef.current

        if (!arena || !button) return

        const area = arena.getBoundingClientRect()
        const current = button.getBoundingClientRect()
        const yes = yesRef.current?.getBoundingClientRect()
        const maxX = Math.max(area.width - current.width, 0)
        const maxY = Math.max(area.height - current.height, 0)
        let next: Point = { x: 0, y: 0 }

        for (let attempt = 0; attempt < 20; attempt += 1) {
            next = { x: Math.random() * maxX, y: Math.random() * maxY }

            const left = area.left + next.x
            const top = area.top + next.y
            const movedEnough = Math.hypot(left - current.left, top - current.top) > 80
            const hitsYes = yes !== undefined
                && left < yes.right + 16
                && left + current.width > yes.left - 16
                && top < yes.bottom + 16
                && top + current.height > yes.top - 16

            if (movedEnough && !hitsYes) break
        }

        setNoPosition(next)
    }

    const sayYes = async (): Promise<void> => {
        if (answered) return

        setAnswered(true)
        stopFireworks.current?.()
        stopFireworks.current = await fireworks(6000)
    }

    return (
        <section id='finale' aria-labelledby='finale-title' className='relative isolate overflow-hidden bg-night py-28 text-white sm:py-36'>
            <div aria-hidden className='pointer-events-none absolute inset-0 -z-10 overflow-hidden'>
                <div className='absolute left-1/2 top-0 size-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/25 blur-3xl' />
                <div className='absolute -bottom-40 -left-20 size-96 rounded-full bg-gold/10 blur-3xl' />

                {STARS.map((star) => (
                    <span
                        key={`${star.top}-${star.left}`}
                        className='absolute animate-twinkle rounded-full bg-white motion-reduce:animate-none'
                        style={{
                            top: star.top,
                            left: star.left,
                            width: star.size,
                            height: star.size,
                            animationDelay: star.delay
                        }}
                    />
                ))}
            </div>

            <div className='mx-auto max-w-3xl px-5 text-center sm:px-8'>
                <Reveal>
                    <span className='mx-auto grid size-16 place-items-center rounded-full bg-white/10 text-rose-300 ring-1 ring-white/15'>
                        <Gem className='size-7' />
                    </span>

                    <h2 id='finale-title' className='mt-7 font-heading text-4xl font-semibold leading-tight text-balance sm:text-6xl'>
                        {FINALE.question}
                    </h2>
                </Reveal>

                {answered ? (
                    <div role='status' className='mt-12 animate-in fade-in zoom-in-95 duration-700 motion-reduce:animate-none'>
                        <p className='font-script text-6xl leading-tight text-rose-300 sm:text-7xl'>
                            {FINALE.yesTitle}
                        </p>

                        <p className='mt-4 font-heading text-2xl text-white text-balance sm:text-3xl'>
                            {FINALE.yesLine}
                        </p>

                        <div className='mx-auto mt-10 max-w-xl rounded-[2rem] border border-white/10 bg-white/5 p-6 text-left shadow-2xl shadow-black/30 backdrop-blur sm:p-8'>
                            <span className='grid size-12 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30'>
                                <Gift className='size-6' />
                            </span>

                            <p className='mt-5 font-hand text-2xl leading-snug text-white/90 sm:text-[1.7rem]'>
                                {FINALE.giftMessage}
                            </p>

                            <Heart aria-hidden className='ml-auto mt-4 size-6 animate-heartbeat fill-rose-300 text-rose-300 motion-reduce:animate-none' />
                        </div>
                    </div>
                ) : (
                    <Reveal delay={150}>
                        <div ref={arenaRef} className='relative mx-auto mt-12 h-56 w-full max-w-xl sm:h-64'>
                            <button
                                ref={yesRef}
                                type='button'
                                onClick={() => void sayYes()}
                                className='absolute left-1/2 top-[34%] inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full bg-primary px-7 py-4 text-base font-bold text-primary-foreground shadow-xl shadow-primary/40 transition hover:scale-105 hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/50 sm:top-1/2 sm:-translate-x-[108%] sm:px-9'
                            >
                                <Heart className='size-5 fill-current' />
                                {FINALE.yes}
                            </button>

                            <button
                                ref={noRef}
                                type='button'
                                onPointerEnter={dodge}
                                onPointerDown={(event) => {
                                    event.preventDefault()
                                    dodge()
                                }}
                                onClick={dodge}
                                className={cn(
                                    'absolute whitespace-nowrap rounded-full border border-white/25 px-7 py-4 text-base font-bold text-white/85 transition-[left,top] duration-300 ease-out focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40 motion-reduce:transition-none',
                                    noPosition === null && 'left-1/2 top-[72%] -translate-x-1/2 -translate-y-1/2 sm:top-1/2 sm:translate-x-[8%]'
                                )}
                                style={noPosition ? { left: noPosition.x, top: noPosition.y } : undefined}
                            >
                                {FINALE.no}
                            </button>
                        </div>
                    </Reveal>
                )}
            </div>
        </section>
    )
}

export default FinaleSection
